// PRD-010 T3.3 (REFACTOR) — Thin wrapper preserving the legacy
// `serializeCommand()` function-style API on top of the new
// {@link CommandSerializer} class (PRD-010 §G2 — Single source of truth).
//
// All serialization logic lives in `command-serializer.ts`. This file
// remains as a stable import path for existing call sites (the element
// and the PRD-009 unit test suite) and re-exports the shared
// `SerializeCommandExtras` contract so there is no duplicate type.
//
// New code should import {@link CommandSerializer} directly. This
// wrapper exists only to avoid a churn-only rename across PRD-009 tests
// and call sites and may be removed once those references are updated.

import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';
import type { SerializedWhiteboardCommand } from '../../elements/whiteboard-command.event.js';
import {
  CommandSerializer,
  type WhiteboardCommand,
  type SerializeCommandExtras,
} from './command-serializer.js';

export type { SerializeCommandExtras } from './command-serializer.js';

/**
 * Serialize one internal package command to the public command-event
 * payload. Returns `null` if the command is not one of the public kinds
 * or if applying it would be a no-op.
 *
 * Thin wrapper over {@link CommandSerializer.serialize} — preserved for
 * backwards compatibility with PRD-009 call sites and the existing unit
 * test suite. The runtime `instanceof` arms inside `CommandSerializer`
 * cover every concrete variant of {@link WhiteboardCommand}; commands
 * outside that union fall through to the DEV warning + `null` return.
 */
export function serializeCommand(
  cmd: Command<BoardState>,
  extras?: SerializeCommandExtras,
): SerializedWhiteboardCommand | null {
  return CommandSerializer.serialize(cmd as WhiteboardCommand, extras);
}
